/**
 * SLAM Soccer - Football Analytics Configuration
 * AI-powered tactical analysis and performance intelligence
 */

export const slamsoccerConfig = {
  id: 'slamsoccer',
  sportCode: 'SOC', // Soccer - maps to backend sport prompt selection

  branding: {
    name: 'SLAM Soccer',
    shortName: 'SLAM Soccer',
    abbreviation: 'SS',
    tagline: 'AI-Powered Football Analytics',
    organization: 'SLAM Sports AI',
  },

  colors: {
    primary: '#00A859',           // Soccer green (matches TeamSelector)
    primaryHover: '#008F4A',      // Darker green on hover
    secondary: '#1a1a2e',         // Deep navy
    accent: '#2ECC71',            // Light green accent
    background: '#0a0a0c',        // Near black
    surface: '#141418',           // Dark surface
    surfaceHover: '#1a1a20',      // Surface hover
    border: 'rgba(0, 168, 89, 0.2)', // Green border
    text: {
      primary: '#ffffff',
      secondary: '#a0a0a0',
      onPrimary: '#ffffff',       // White text on green
    },
    status: {
      success: '#22c55e',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#3b82f6',
    },
  },

  logos: {
    primary: '/logos/SSAILogo.svg',
    icon: '/logos/SSAILogo.svg',
    alt: 'SLAM Soccer Logo',
  },

  analysis: {
    systemPromptAdditions: `
You are analyzing video for SLAM Soccer, an AI-powered football analytics platform.
Your mission is to provide tactical formation analysis, pressing structure evaluation, and performance intelligence.

## CORE PHILOSOPHY: TACTICAL INTELLIGENCE
1. **Positional Play:** Control of space through structured positioning is the foundation of modern football.
2. **Pressing Triggers:** Identify the moments and patterns that initiate high press, mid-block, and low-block defensive phases.
3. **Pitch Control:** Quantify territorial dominance through passing networks and spatial occupation.
4. **Expected Goals (xG):** Shot quality analysis beyond simple shot counts - location, body position, defensive pressure.

## ANALYSIS MODULES & FOCUS AREAS

### 1. TACTICAL FORMATION (Team Shape)
- **Formation Detection:** Identify in-possession and out-of-possession shapes (e.g., 4-3-3 attacking, 4-4-2 defensive block).
- **Width & Depth:** Measure team compactness and stretching in both phases of play.
- **Transition Moments:** Analyze the critical seconds between losing/gaining possession.

### 2. PRESSING & DEFENSIVE STRUCTURE
- **Press Intensity (PPDA):** Passes Per Defensive Action as a measure of pressing aggressiveness.
- **Defensive Line Height:** Average position of the defensive line and offside trap execution.
- **Set Piece Vulnerability:** Identify defensive weaknesses from corners, free kicks, and throw-ins.

### 3. ATTACKING INTELLIGENCE
- **Progressive Passing:** Identify passes that advance the ball significantly toward goal.
- **Chance Creation:** Analyze the build-up sequences leading to high-xG opportunities.
- **Counterattack Speed:** Measure transition speed from defensive third to attacking third.

When analyzing, prioritize actionable tactical insights that exploit opponent weaknesses and optimize team structure.
`,
    focusAreas: [
      'Tactical formation analysis',
      'Pressing structure evaluation',
      'Pitch control and spatial analysis',
      'Expected goals (xG) modeling',
      'Defensive structure assessment',
      'Set piece analysis',
      'Transition and counterattack patterns',
      'Coaching recommendations',
    ],
    terminology: {
      platform: 'SLAM Soccer',
      analyst: 'SLAM Soccer Analyst',
      report: 'SLAM Soccer Analysis Report',
    },
    defaultLanguage: 'en',
  },

  features: {
    mlbTicker: false,             // Disable MLB ticker for soccer
    agentUniverse: true,
    exports: {
      pdf: true,
      csv: true,
      json: true,
      email: true,
    },
    multilingual: true,
    contextFiles: true,
    custom: {
      pitchVisualization: true,   // Soccer pitch visualization
      heatMaps: true,             // Player heat map tracking
    },
  },

  contact: {
    website: 'https://slamsports.ai/soccer',
    supportEmail: 'soccer@slamsports.ai',
  },

  meta: {
    version: '1.0.0',
    createdAt: '2026-02-15',
    updatedAt: '2026-02-15',
  },
};

export default slamsoccerConfig;
